import * as React from 'react';
import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import type { ErrorPolicy } from '@apollo/client';
import { captureException } from '@sentry/react';
import type { PageConfig_PageName as PageName } from '__generated__/graphql-types';

import { useParams } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import { SKILLS_IDS_FOR_CLIPS_ALLOW_LIST } from 'bundles/enterprise-legacy-learner-home/constants/skillCollectionsConstants';
import GrowthDiscoveryExperiments from 'bundles/epic/clients/GrowthDiscovery';
import ComponentsConfigContext from 'bundles/page-config-common/contexts/ComponentsConfigContext';
import { usePageContext } from 'bundles/page-config-common/contexts/PageContext';
import { useEnterpriseOnboardingData } from 'bundles/page-config-common/contexts/enterprise/EnterpriseOnboardingContext';
import SandboxDataProvider from 'bundles/page-config-common/providers/SandboxDataProvider';
import { PageConfigFallbackData } from 'bundles/page-config-common/queries/mock-data/PageConfigComponentsFallbackData';
import { PageConfigMockData } from 'bundles/page-config-common/queries/mock-data/PageConfigComponentsMockData';
import PageConfigComponentsQuery from 'bundles/page-config-common/queries/page-config/PageConfigComponentsQuery.graphql';
import type {
  PageConfigComponentsQuery as PageConfigComponentsQueryType,
  PageConfigComponentsQueryVariables,
} from 'bundles/page-config-common/queries/page-config/__generated__/PageConfigComponentsQuery';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { MAX_ONBOARDING_SKILLS_LENGTH } from 'bundles/unified-home-common/constants';

type Props = {
  children: React.ReactNode;
  pageName: PageName;
  limit?: number;
  additionalInput?: Record<string, string>;
};

const useEnterpriseComponentsConfigOptions = (
  pageName: PageName,
  limit: number,
  additionalInput?: Record<string, string>
) => {
  const showSandboxMode = GrowthDiscoveryExperiments.get('showUnifiedHomeSandbox');
  const { programSlug } = useParams();
  const program = useProgram();
  const { enterpriseOnboardingDataLoading, skills } = useEnterpriseOnboardingData();
  const filteredSkills = useMemo(
    () => skills.filter((skill) => SKILLS_IDS_FOR_CLIPS_ALLOW_LIST.includes(skill.id)),
    [skills]
  );
  const skillAdditionalInput = useMemo(() => {
    if (filteredSkills.length) {
      return filteredSkills.slice(0, MAX_ONBOARDING_SKILLS_LENGTH).reduce((acc, skill, index) => {
        acc[`skill-based-clips-${index + 1}.skillName`] = skill.name;
        return acc;
      }, {} as Record<string, string>);
    } else {
      return undefined;
    }
  }, [filteredSkills]);

  return {
    variables: {
      pageName,
      programSlug,
      limit,
      programId: program?.id as string,
      shouldQueryEnterpriseLoggedInData: isAuthenticatedUser(),
      additionalInput: {
        ...additionalInput,
        ...skillAdditionalInput,
      },
    },
    context: {
      clientName: 'gatewayGql',
    },
    skip: showSandboxMode || (isAuthenticatedUser() && enterpriseOnboardingDataLoading) || !program?.id,
    errorPolicy: 'all' as ErrorPolicy,
    notifyOnNetworkStatusChange: true,
  };
};

const useConsumerComponentsConfigOptions = (
  pageName: PageName,
  limit: number,
  additionalInput?: Record<string, string>
) => {
  const showSandboxMode = GrowthDiscoveryExperiments.get('showUnifiedHomeSandbox');

  return {
    variables: {
      pageName,
      limit,
      // In enterprise context, some fields require a program ID. To avoid creating multiple providers/contexts/queries for both enterprise and consumer,
      // pass in empty string in conusmer context. We use an @include directive in the query to prevent querying this field on consumer pages, but passing
      // in a defined value is still required.
      programId: '',
      shouldQueryEnterpriseLoggedInData: false,
      ...(additionalInput ? { additionalInput: { ...additionalInput } } : {}),
    },
    context: {
      clientName: 'gatewayGql',
    },
    skip: showSandboxMode,
    errorPolicy: 'all' as ErrorPolicy,
    notifyOnNetworkStatusChange: true,
  };
};

/**
 * @description This provider is used to fetch and provide the components config data for a given page.
 */
const ComponentsConfigProvider = ({ children, pageName, limit = 25, additionalInput }: Props) => {
  const showSandboxMode = GrowthDiscoveryExperiments.get('showUnifiedHomeSandbox');
  const consumerConfigOptions = useConsumerComponentsConfigOptions(pageName, limit, additionalInput);
  const enterpriseConfigOptions = useEnterpriseComponentsConfigOptions(pageName, limit, additionalInput);
  const pageContext = usePageContext();

  const { data, error, loading, refetch, fetchMore, networkStatus } = useQuery<
    PageConfigComponentsQueryType,
    PageConfigComponentsQueryVariables
  >(PageConfigComponentsQuery, pageContext?.isEnterprise ? enterpriseConfigOptions : consumerConfigOptions);

  // In Sandbox Mode, render mock data and execute mock data providers before moving them into the productionized HomePageProvider.
  if (showSandboxMode && !!PageConfigMockData[pageName]) {
    return (
      <ComponentsConfigContext.Provider
        value={{
          components: PageConfigMockData[pageName],
          refetch: () => '',
        }}
      >
        <SandboxDataProvider>{children}</SandboxDataProvider>
      </ComponentsConfigContext.Provider>
    );
  }

  // If there is an error fetching data or no components returned, render the fallback data.
  if (!loading && !data?.PageConfig?.queryPaginatedComponents?.elements && PageConfigFallbackData[pageName]) {
    captureException(error, {
      extra: { message: 'Error fetching page config components data' },
    });

    return (
      <ComponentsConfigContext.Provider
        value={{
          components: PageConfigFallbackData[pageName],
          refetch: () => '',
        }}
      >
        {children}
      </ComponentsConfigContext.Provider>
    );
  }

  return (
    <ComponentsConfigContext.Provider
      value={{
        components: data?.PageConfig?.queryPaginatedComponents?.elements,
        pagination: {
          cursor: data?.PageConfig?.queryPaginatedComponents?.pagination?.cursor,
          componentListMetadataHash: data?.PageConfig?.queryPaginatedComponents?.componentListMetadataHash,
        },
        refetch,
        fetchMore,
        loading: {
          isInitiallyLoading: networkStatus === 1,
          isFetchMoreLoading: networkStatus === 3,
        },
      }}
    >
      {children}
    </ComponentsConfigContext.Provider>
  );
};

export default ComponentsConfigProvider;
